import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Alert, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ReceiptIcon from '@mui/icons-material/Receipt';

import CustomCard from '../../Shared/Components/CustomCard/CustomCard';
import Status from '../../Shared/Components/Status/Status';
import SaveButtons from '../../Shared/Components/SaveButtons/SaveButtons';

import { CashDocumentFormSection } from '../Components';
import {
  createCashDocumentSchema,
  type CreateCashDocumentFormData,
} from '../Validation/cashDocument.schema';
import {
  useCreateCashDocumentMutation,
  useUpdateCashDocumentMutation,
  useGetCashDocumentByIdQuery,
} from '../Service/cashDocumentsApi';
import { CashDocumentType, type CreateCashDocumentRequest } from '../Types/cashDocumentTypes';
import { getCashDocumentsErrorMessage } from '../Utils/cashDocumentsErrorMapping';
import { getCashDocumentsCompanyScopeErrorMessage } from '../Service/cashDocumentsErrorMapping';
import { resolveCashDocumentRedirectPath } from '../Utils/cashDocumentNavigation';
import {
  useGetReservationPartiesLookupQuery,
  useGetReservationWithAssociationsByIdQuery,
} from '../../Reservation/Service/extendedReservationsApi';

const formatPurposeDate = (date?: string): string | undefined => {
  if (!date) return undefined;

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return new Intl.DateTimeFormat('pl-PL').format(parsed);
};

const CreateCashDocument: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();

  const isEditMode = !!id;
  const parsedId = id ? parseInt(id, 10) : undefined;
  const parsedReservationId = searchParams.get('reservationId')
    ? parseInt(searchParams.get('reservationId')!, 10)
    : undefined;
  const parsedInvoiceId = searchParams.get('invoiceId')
    ? parseInt(searchParams.get('invoiceId')!, 10)
    : undefined;
  const returnTo = searchParams.get('returnTo');
  const hasAppliedReservationPrefill = useRef(false);
  const redirectPath = useMemo(() => resolveCashDocumentRedirectPath(returnTo), [returnTo]);

  const isManualMode = !parsedReservationId && !parsedInvoiceId && !isEditMode;
  const [companyId, setCompanyId] = React.useState<number | null>(null);
  const [companyError, setCompanyError] = React.useState<string | undefined>(undefined);

  // Fetch existing document if editing
  const {
    data: existingDocument,
    isLoading: isLoadingDocument,
    error: fetchError,
  } = useGetCashDocumentByIdQuery(parsedId!, {
    skip: !isEditMode,
  });
  const { data: reservationPrefillData } = useGetReservationWithAssociationsByIdQuery(
    parsedReservationId!,
    {
      skip: isEditMode || !parsedReservationId,
    }
  );
  const applicantPartyId =
    typeof reservationPrefillData?.applicantPartyId === 'number'
      ? reservationPrefillData.applicantPartyId
      : undefined;
  const invoiceBuyerPartyId =
    typeof reservationPrefillData?.invoiceBuyerPartyId === 'number'
      ? reservationPrefillData.invoiceBuyerPartyId
      : undefined;
  const prefillPartyId =
    invoiceBuyerPartyId !== undefined && invoiceBuyerPartyId !== applicantPartyId
      ? invoiceBuyerPartyId
      : applicantPartyId;
  const {
    data: applicantParties = [],
    isLoading: isApplicantPartiesLoading,
  } = useGetReservationPartiesLookupQuery(
    prefillPartyId ? { ids: [prefillPartyId], take: 1 } : undefined,
    {
      skip: isEditMode || !prefillPartyId,
    }
  );

  // Mutations
  const [createCashDocument, createStatus] = useCreateCashDocumentMutation();
  const [updateCashDocument, updateStatus] = useUpdateCashDocumentMutation();

  const mutationStatus = isEditMode ? updateStatus : createStatus;
  const mutationErrorMessage = mutationStatus.isError
    ? getCashDocumentsCompanyScopeErrorMessage(
        mutationStatus.error,
        isEditMode
          ? 'Nie udało się zaktualizować dokumentu kasowego.'
          : 'Nie udało się utworzyć dokumentu kasowego.'
      )
    : undefined;

  // React Hook Form setup
  const {
    control,
    handleSubmit: rhfHandleSubmit,
    watch,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<CreateCashDocumentFormData>({
    defaultValues: {
      type: CashDocumentType.KP,
      amount: 0,
      currency: 'PLN',
      documentDate: new Date().toISOString().slice(0, 10),
      personName: '',
      purpose: '',
      note: '',
      reservationId: parsedReservationId || null,
      invoiceId: parsedInvoiceId || null,
      issuedBy: '',
    },
    resolver: yupResolver(createCashDocumentSchema) as any,
    mode: 'onChange',
  });
  const prefillPersonName = useMemo(() => {
    const partyLabel = applicantParties[0]?.displayName?.trim();
    if (partyLabel) {
      return partyLabel;
    }

    const nameFromLegacyFields = [
      reservationPrefillData?.applicantFirstName?.trim(),
      reservationPrefillData?.applicantLastName?.trim(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (nameFromLegacyFields) {
      return nameFromLegacyFields;
    }

    return [
      reservationPrefillData?.applicant?.firstName?.trim(),
      reservationPrefillData?.applicant?.lastName?.trim(),
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
  }, [applicantParties, reservationPrefillData]);
  const prefillPurpose = useMemo(() => {
    const offerName = reservationPrefillData?.offerName?.trim();
    const dateFrom = formatPurposeDate(
      reservationPrefillData?.dateFrom ?? reservationPrefillData?.booking?.bookingDateFrom
    );
    const dateTo = formatPurposeDate(
      reservationPrefillData?.dateTo ?? reservationPrefillData?.booking?.bookingDateTo
    );

    if (offerName && dateFrom && dateTo) {
      return `${offerName} ${dateFrom} - ${dateTo}`;
    }
    if (offerName && dateFrom) {
      return `${offerName} od ${dateFrom}`;
    }
    if (offerName && dateTo) {
      return `${offerName} do ${dateTo}`;
    }
    if (offerName) {
      return offerName;
    }
    return '';
  }, [reservationPrefillData]);

  // Watch fields for conditional rendering
  const watchType = watch('type');
  const watchInvoiceId = watch('invoiceId');

  // Auto-fill form when editing
  useEffect(() => {
    if (isEditMode && existingDocument) {
      console.log('[CreateCashDocument] Auto-filling form with existing data:', existingDocument);
      reset({
        type: existingDocument.type as CashDocumentType,
        amount: existingDocument.amount,
        currency: existingDocument.currency,
        documentDate: existingDocument.documentDate,
        personName: existingDocument.personName,
        purpose: existingDocument.purpose,
        note: existingDocument.note || '',
        reservationId: existingDocument.reservationId || null,
        invoiceId: existingDocument.invoiceId || null,
        issuedBy: existingDocument.issuedBy || '',
      });
      setCompanyId(existingDocument.companyId ?? null);
    }
  }, [isEditMode, existingDocument, reset]);

  // Prefill reservation-context fields for KP/KW create flow.
  useEffect(() => {
    if (
      isEditMode ||
      !parsedReservationId ||
      !reservationPrefillData ||
      hasAppliedReservationPrefill.current
    ) {
      return;
    }

    if (applicantPartyId && isApplicantPartiesLoading) {
      return;
    }

    const currentPersonName = getValues('personName')?.trim();
    const currentPurpose = getValues('purpose')?.trim();

    if (!currentPersonName && prefillPersonName) {
      setValue('personName', prefillPersonName, { shouldValidate: true });
    }

    if (!currentPurpose && prefillPurpose) {
      setValue('purpose', prefillPurpose, { shouldValidate: true });
    }

    hasAppliedReservationPrefill.current = true;
  }, [
    applicantPartyId,
    getValues,
    isApplicantPartiesLoading,
    isEditMode,
    parsedReservationId,
    prefillPersonName,
    prefillPurpose,
    reservationPrefillData,
    setValue,
  ]);

  // Submit handler
  const onSubmit = useCallback(
    async (formData: CreateCashDocumentFormData, shouldRedirect = true) => {
      console.log('[CreateCashDocument] Submitting form:', {
        isEditMode,
        formData,
      });

      // Validate company for manual create flow
      if (isManualMode && (companyId === null || companyId === undefined)) {
        setCompanyError('Firma jest wymagana dla dokumentu kasowego.');
        return;
      }
      setCompanyError(undefined);

      // Convert null to undefined for API compatibility
      const apiPayload = {
        ...formData,
        note: formData.note || undefined,
        reservationId: formData.reservationId || undefined,
        invoiceId: formData.invoiceId || undefined,
        issuedBy: formData.issuedBy || undefined,
        companyId: companyId ?? undefined,
      };

      console.log('[CreateCashDocument] API Payload being sent:', apiPayload);
      console.log(
        '[CreateCashDocument] Type value:',
        apiPayload.type,
        'Type of type:',
        typeof apiPayload.type
      );

      try {
        if (isEditMode && parsedId) {
          // Update existing document
          // Backend update command currently requires id in body during deserialization.
          const updateData = {
            id: parsedId,
            amount: apiPayload.amount,
            documentDate: apiPayload.documentDate,
            personName: apiPayload.personName,
            purpose: apiPayload.purpose,
            note: apiPayload.note,
            issuedBy: apiPayload.issuedBy,
          };
          const updatePayload = {
            id: parsedId,
            data: updateData,
          };
          await updateCashDocument(updatePayload).unwrap();
          console.log('[CreateCashDocument] Document updated successfully');
        } else {
          // Create new document
          await createCashDocument(apiPayload as CreateCashDocumentRequest).unwrap();
          console.log('[CreateCashDocument] Document created successfully');
        }

        if (shouldRedirect) {
          setTimeout(() => {
            navigate(-1);
          }, 1500);
        }
      } catch (error) {
        console.error('[CreateCashDocument] Submission error:', error);
      }
    },
    [isEditMode, parsedId, companyId, isManualMode, createCashDocument, updateCashDocument, navigate]
  );

  // Loading state
  if (isEditMode && isLoadingDocument) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <Typography>Ładowanie dokumentu...</Typography>
      </Box>
    );
  }

  // Error state
  if (isEditMode && fetchError) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">
          {getCashDocumentsErrorMessage(
            fetchError,
            'Nie udało się załadować dokumentu. Sprawdź połączenie i spróbuj ponownie.'
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={rhfHandleSubmit((data) => onSubmit(data, true))}
      sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}
    >
      {/* Header Card */}
      <CustomCard
        title={isEditMode ? 'Edytuj dokument kasowy' : 'Utwórz dokument kasowy'}
        icon={<ReceiptIcon />}
      >
        <Box sx={{ mb: 2 }}>
          <Status
            isLoading={mutationStatus.isLoading}
            isError={mutationStatus.isError}
            isSuccess={mutationStatus.isSuccess}
            error={mutationStatus.error}
            errorMessage={mutationErrorMessage}
          />
        </Box>

        {/* Info Alert for Context */}
        {parsedReservationId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Dokument zostanie powiązany z rezerwacją <strong>#{parsedReservationId}</strong>
            </Typography>
          </Alert>
        )}

        {parsedInvoiceId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Dokument zostanie powiązany z fakturą <strong>#{parsedInvoiceId}</strong> i
              automatycznie zarejestrowany jako płatność.
            </Typography>
          </Alert>
        )}

        {/* Main Form Section */}
        <CashDocumentFormSection
          control={control}
          errors={errors}
          watchType={watchType}
          watchInvoiceId={watchInvoiceId}
          showCompanySelect={isManualMode}
          companyId={companyId}
          onCompanyChange={(id) => {
            setCompanyId(id);
            setCompanyError(undefined);
          }}
          companyError={companyError}
        />

        {/* Save Buttons */}
        <Box sx={{ mt: 3 }}>
          <SaveButtons
            handleSubmit={async (e, shouldRedirect) => {
              e.preventDefault();
              await rhfHandleSubmit((data) => onSubmit(data, shouldRedirect))();
            }}
            isUpdating={mutationStatus.isLoading}
            handleCancel={() => navigate(redirectPath)}
          />
        </Box>
      </CustomCard>
    </Box>
  );
};

export default CreateCashDocument;
